import { useEffect, useRef, useState, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react'
import { Maximize2, RotateCcw, ZoomIn, ZoomOut } from 'lucide-react'
import { generateBeadPatternFromEditorImage } from '../utils/imageProcessor'

const EDITOR_SIZE = 512
const MIN_SCALE = 0.05
const MAX_SCALE = 20

function drawPattern(canvas, pattern, brand) {
  if (!pattern || !canvas) return
  const { grid, width, height } = pattern
  const cell = Math.max(4, Math.floor(canvas.width / width))
  canvas.width = width * cell
  canvas.height = height * cell
  const ctx = canvas.getContext('2d')
  const colorMap = new Map(brand.colors.map((c) => [c.id, c.hex]))

  ctx.fillStyle = '#fffdf8'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const hex = colorMap.get(grid[y][x])
      if (!hex) continue
      ctx.fillStyle = hex
      ctx.fillRect(x * cell, y * cell, cell, cell)
    }
  }
}

function drawGridLines(ctx, x, y, w, h, cell, group = 5) {
  const cols = Math.ceil(w / cell)
  const rows = Math.ceil(h / cell)
  // 细网格
  ctx.strokeStyle = 'rgba(93, 78, 109, 0.25)'
  ctx.lineWidth = 1
  for (let i = 0; i <= cols; i++) {
    const pos = x + i * cell
    ctx.beginPath()
    ctx.moveTo(pos, y)
    ctx.lineTo(pos, y + h)
    ctx.stroke()
  }
  for (let i = 0; i <= rows; i++) {
    const pos = y + i * cell
    ctx.beginPath()
    ctx.moveTo(x, pos)
    ctx.lineTo(x + w, pos)
    ctx.stroke()
  }
  // 每 5×5 一组粗线（模拟拼豆板规格）
  ctx.strokeStyle = 'rgba(93, 78, 109, 0.55)'
  ctx.lineWidth = 2
  for (let i = 0; i <= cols; i += group) {
    const pos = x + i * cell
    ctx.beginPath()
    ctx.moveTo(pos, y)
    ctx.lineTo(pos, y + h)
    ctx.stroke()
  }
  for (let i = 0; i <= rows; i += group) {
    const pos = y + i * cell
    ctx.beginPath()
    ctx.moveTo(x, pos)
    ctx.lineTo(x + w, pos)
    ctx.stroke()
  }
}

const BeadEditor = forwardRef(function BeadEditor({
  src,
  gridSize,
  brand,
  maxColors,
  dithering,
  mergeThreshold,
  pixelMode,
  denoise = 0,
  faithful = false,
  saturation = 1,
  preprocessStrength = 0,
  outline = true,
  removeBackground = false,
  locked = false,
  onGenerate,
  onGridSizeChange,
  onCellEditChange,
  onLiveGridSizeChange,
  initialState = null,
  onStateChange,
  headerControl = null,
  toolbarControl = null,
}, ref) {
  const canvasRef = useRef(null)
  const previewCanvasRef = useRef(null)
  const initialStateRef = useRef(initialState)
  const restoredLockAnchorRef = useRef(initialState?.lockAnchor || null)
  const previousGridSizeRef = useRef(gridSize)
  const [img, setImg] = useState(null)
  const [transform, setTransform] = useState(initialState?.transform || { x: 0, y: 0, scale: 1 })
  const [dragging, setDragging] = useState(false)
  const [lastPos, setLastPos] = useState({ x: 0, y: 0 })
  const [previewPattern, setPreviewPattern] = useState(null)
  const [cellEditMode, setCellEditMode] = useState(Boolean(initialState?.cellEditMode))
  const [cellSize, setCellSize] = useState(() => initialState?.cellSize || EDITOR_SIZE / gridSize)
  const [resizingCell, setResizingCell] = useState(false)
  // 锁定锚点：以原图内容坐标锁定无限网格 —— cellContent 为单粒对应的原图像素尺寸，
  // phaseX/phaseY 为网格线相对原图左上角的相位（原图像素），锁定后网格与图片作为整体随缩放/移动
  const [lockAnchor, setLockAnchor] = useState(initialState?.lockAnchor || null)
  const touchRef = useRef(null)
  const debounceRef = useRef(null)
  // 单粒大小引导提示
  const [showSizeGuide, setShowSizeGuide] = useState(initialState?.showSizeGuide ?? true)

  // 暴露 cellEditMode 给父组件
  useEffect(() => {
    onCellEditChange?.(cellEditMode)
  }, [cellEditMode, onCellEditChange])


  useEffect(() => {
    if (!src) return
    let cancelled = false
    const isLocal = src.startsWith('data:') || src.startsWith('blob:')
    const image = new Image()
    if (!isLocal) {
      image.crossOrigin = 'anonymous'
    }
    image.onload = () => {
      if (cancelled) return
      setImg(image)
      const scale = EDITOR_SIZE / Math.max(image.width, image.height)
      const savedState = initialStateRef.current
      setTransform(savedState?.transform || { x: 0, y: 0, scale })
      initialStateRef.current = null
    }
    image.onerror = () => {
      if (!cancelled) console.error('[BeadEditor] failed to load image:', src.slice(0, 100))
    }
    image.src = src
    return () => {
      cancelled = true
      image.onload = null
      image.onerror = null
    }
  }, [src])

  // gridSize 后续变化时同步单粒编辑默认值；首次挂载保留草稿中的 cellSize。
  useEffect(() => {
    if (previousGridSizeRef.current !== gridSize) {
      previousGridSizeRef.current = gridSize
      setCellSize(Math.max(40, EDITOR_SIZE / gridSize))
    }
  }, [gridSize])

  // 锁定瞬间捕获锚点；解锁时清除
  useEffect(() => {
    if (!locked) {
      setLockAnchor(null)
      return
    }
    if (!img) return
    if (restoredLockAnchorRef.current) {
      setLockAnchor(restoredLockAnchorRef.current)
      restoredLockAnchorRef.current = null
      return
    }
    // 在当前视角下锁定：无限网格的比例与位置固定到原图内容上
    const t = transform
    const baseCell = cellEditMode
      ? EDITOR_SIZE / Math.max(5, Math.round(EDITOR_SIZE / cellSize))
      : EDITOR_SIZE / gridSize
    const cellContent = baseCell / t.scale
    const imgX = EDITOR_SIZE / 2 + t.x - (img.width * t.scale) / 2
    const imgY = EDITOR_SIZE / 2 + t.y - (img.height * t.scale) / 2
    // 网格线（当前从画布 0,0 起）在原图内容坐标中的相位
    const phaseX = (((-imgX) / t.scale) % cellContent + cellContent) % cellContent
    const phaseY = (((-imgY) / t.scale) % cellContent + cellContent) % cellContent
    setLockAnchor({ cellContent, phaseX, phaseY })
    // 锁定后重置位置：回到导入时的初始视角，便于对比原图与生成图的初始状态
    // （网格以内容坐标锁定，重置后仍与图片内容保持一致）
    const resetScale = EDITOR_SIZE / Math.max(img.width, img.height)
    setTransform({ x: 0, y: 0, scale: resetScale })
    // 仅在锁定瞬间捕获一次，不随 transform 变化重捕
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locked, img])


  useEffect(() => {
    onStateChange?.({
      transform,
      cellEditMode,
      cellSize,
      lockAnchor,
      showSizeGuide,
    })
  }, [transform, cellEditMode, cellSize, lockAnchor, showSizeGuide, onStateChange])
  // 锁定后的实时几何：单粒屏幕尺寸 + 网格相位（屏幕坐标），随缩放/移动实时变化
  const getLockGeometry = useCallback(() => {
    if (!locked || !lockAnchor || !img) return null
    const imgW = img.width * transform.scale
    const imgH = img.height * transform.scale
    const imgX = EDITOR_SIZE / 2 + transform.x - imgW / 2
    const imgY = EDITOR_SIZE / 2 + transform.y - imgH / 2
    const cell = Math.max(0.5, lockAnchor.cellContent * transform.scale)
    return {
      cell,
      originX: imgX + lockAnchor.phaseX * transform.scale,
      originY: imgY + lockAnchor.phaseY * transform.scale,
      imgX,
      imgY,
      imgW,
      imgH,
    }
  }, [locked, lockAnchor, img, transform])

  const buildPreviewPattern = useCallback(() => {
    if (!img) return null

    const options = {
      brand,
      maxColors,
      dithering,
      mergeThreshold,
      pixelMode,
      denoise,
      faithful,
      saturation,
      preprocessStrength,
      outline,
      removeBackground,
    }

    // 锁定模式：无限网格已锁定在原图内容上，板子规格由当前缩放反推
    const lockGeo = getLockGeometry()
    if (lockGeo) {
      const { cell, originX, originY } = lockGeo
      const n = Math.max(1, Math.round(EDITOR_SIZE / cell))
      // 以网格相位为基准，从画布左上方最近的格线开始采样
      const startX = originX + Math.floor((0 - originX) / cell) * cell
      const startY = originY + Math.floor((0 - originY) / cell) * cell
      return generateBeadPatternFromEditorImage(img, transform, n, EDITOR_SIZE, {
        ...options,
        gridAnchor: {
          gridSize: n,
          cellSize: cell,
          offsetX: startX,
          offsetY: startY,
        },
      })
    }

    const effectiveGridSize = cellEditMode
      ? Math.max(5, Math.round(EDITOR_SIZE / cellSize))
      : gridSize
    return generateBeadPatternFromEditorImage(img, transform, effectiveGridSize, EDITOR_SIZE, options)
  }, [
    img,
    transform,
    gridSize,
    cellEditMode,
    cellSize,
    getLockGeometry,
    brand,
    maxColors,
    dithering,
    mergeThreshold,
    pixelMode,
    denoise,
    faithful,
    saturation,
    preprocessStrength,
    outline,
    removeBackground,
  ])

  const updatePreview = useCallback(() => {
    const pattern = buildPreviewPattern()
    if (!pattern) return
    setPreviewPattern(pattern)
    drawPattern(previewCanvasRef.current, pattern, brand)
  }, [buildPreviewPattern, brand])

  // 参数或图片缩放变化时实时预览（防抖 100ms）
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(updatePreview, 100)
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
        debounceRef.current = null
      }
    }
  }, [updatePreview, transform.scale, transform.x, transform.y])

  const draw = useCallback(() => {
    if (!img || !canvasRef.current) return
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, EDITOR_SIZE, EDITOR_SIZE)

    const imgX = EDITOR_SIZE / 2 + transform.x - (img.width * transform.scale) / 2
    const imgY = EDITOR_SIZE / 2 + transform.y - (img.height * transform.scale) / 2
    const imgW = img.width * transform.scale
    const imgH = img.height * transform.scale
    ctx.drawImage(img, imgX, imgY, imgW, imgH)

    // 参考线间距：始终以“输出图纸格子”为唯一基准（512/格数），不随图片缩放变化，
    // 与采样逻辑（buildPreviewPattern / handleGenerate）严格一致；锁定后跟随锚点内容
    const lockGeo = getLockGeometry()
    const effectiveCell = cellEditMode
      ? EDITOR_SIZE / Math.max(5, Math.round(EDITOR_SIZE / cellSize))
      : EDITOR_SIZE / gridSize
    const baseCell = lockGeo ? lockGeo.cell : effectiveCell
    let gridX = 0
    let gridY = 0
    if (lockGeo) {
      // 以锁定在原图上的网格相位为基准绘制无限网格
      gridX = ((lockGeo.originX % baseCell) + baseCell) % baseCell
      gridY = ((lockGeo.originY % baseCell) + baseCell) % baseCell
    }
    drawGridLines(ctx, gridX, gridY, EDITOR_SIZE - gridX, EDITOR_SIZE - gridY, baseCell)
    ctx.strokeStyle = 'rgba(93, 78, 109, 0.5)'
    ctx.lineWidth = 2
    ctx.strokeRect(0, 0, EDITOR_SIZE, EDITOR_SIZE)

    if (cellEditMode) {
      // 锁定后方框对齐到内容网格（跟随缩放）；未锁定时 snap 到采样格
      const box = lockGeo ? lockGeo.cell : effectiveCell
      const half = box / 2
      const frameX = lockGeo
        ? lockGeo.originX + Math.round((EDITOR_SIZE / 2 - half - lockGeo.originX) / box) * box
        : Math.round((EDITOR_SIZE / 2 - half) / box) * box
      const frameY = lockGeo
        ? lockGeo.originY + Math.round((EDITOR_SIZE / 2 - half - lockGeo.originY) / box) * box
        : Math.round((EDITOR_SIZE / 2 - half) / box) * box
      ctx.strokeStyle = '#ff4d6d'
      ctx.lineWidth = 3
      ctx.strokeRect(frameX, frameY, box, box)

      // 中心加粗田字格（十字线）
      ctx.lineWidth = 2
      ctx.beginPath()
      ctx.moveTo(frameX + half, frameY)
      ctx.lineTo(frameX + half, frameY + box)
      ctx.stroke()
      ctx.beginPath()
      ctx.moveTo(frameX, frameY + half)
      ctx.lineTo(frameX + box, frameY + half)
      ctx.stroke()

      // 右下角拖动手柄（更大更易点击）
      ctx.fillStyle = '#ff4d6d'
      ctx.beginPath()
      ctx.arc(frameX + box, frameY + box, 10, 0, Math.PI * 2)
      ctx.fill()
      ctx.fillStyle = '#ffffff'
      ctx.beginPath()
      ctx.arc(frameX + box, frameY + box, 4, 0, Math.PI * 2)
      ctx.fill()
    }
  }, [img, transform, gridSize, cellEditMode, cellSize, getLockGeometry])

  useEffect(() => {
    draw()
  }, [draw])

  const toEditorPoint = (clientX, clientY) => {
    const rect = canvasRef.current.getBoundingClientRect()
    return {
      x: (clientX - rect.left) * (EDITOR_SIZE / rect.width),
      y: (clientY - rect.top) * (EDITOR_SIZE / rect.height),
    }
  }

  const getCellFrame = () => {
    const lockGeo = getLockGeometry()
    if (lockGeo) {
      const c = lockGeo.cell
      return {
        x: lockGeo.originX + Math.round((EDITOR_SIZE / 2 - c / 2 - lockGeo.originX) / c) * c,
        y: lockGeo.originY + Math.round((EDITOR_SIZE / 2 - c / 2 - lockGeo.originY) / c) * c,
        w: c,
        h: c,
      }
    }
    // 与 draw 中的 snap 逻辑保持一致，保证手柄命中位置 = 视觉位置
    const cell = EDITOR_SIZE / Math.max(5, Math.round(EDITOR_SIZE / cellSize))
    return {
      x: Math.round((EDITOR_SIZE / 2 - cell / 2) / cell) * cell,
      y: Math.round((EDITOR_SIZE / 2 - cell / 2) / cell) * cell,
      w: cell,
      h: cell,
    }
  }

  const isNearHandle = (point) => {
    const f = getCellFrame()
    const hx = f.x + f.w
    const hy = f.y + f.h
    return Math.hypot(point.x - hx, point.y - hy) < 12
  }

  const handleWheel = useCallback((e) => {
    e.preventDefault()
    const point = toEditorPoint(e.clientX, e.clientY)
    const factor = e.deltaY < 0 ? 1.1 : 0.9
    setTransform((prev) => {
      const newScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, prev.scale * factor))
      return {
        x: prev.x + (point.x - EDITOR_SIZE / 2) * (1 - newScale / prev.scale),
        y: prev.y + (point.y - EDITOR_SIZE / 2) * (1 - newScale / prev.scale),
        scale: newScale,
      }
    })
  }, [])

  const handleMouseDown = (e) => {
    const point = toEditorPoint(e.clientX, e.clientY)
    if (cellEditMode && !locked && isNearHandle(point)) {
      setResizingCell(true)
      return
    }
    setDragging(true)
    setLastPos({ x: e.clientX, y: e.clientY })
  }

  const handleMouseMove = (e) => {
    if (resizingCell) {
      const point = toEditorPoint(e.clientX, e.clientY)
      const dx = point.x - EDITOR_SIZE / 2
      const dy = point.y - EDITOR_SIZE / 2
      const newSize = Math.max(4, Math.min(EDITOR_SIZE, Math.max(Math.abs(dx), Math.abs(dy)) * 2))
      setCellSize(newSize)
      return
    }
    if (!dragging) return
    const rect = canvasRef.current.getBoundingClientRect()
    const dx = (e.clientX - lastPos.x) * (EDITOR_SIZE / rect.width)
    const dy = (e.clientY - lastPos.y) * (EDITOR_SIZE / rect.height)
    setTransform((prev) => ({ ...prev, x: prev.x + dx, y: prev.y + dy }))
    setLastPos({ x: e.clientX, y: e.clientY })
  }

  const stopDrag = () => {
    setDragging(false)
    setResizingCell(false)
  }

  const handleReset = () => {
    if (!img) return
    const scale = EDITOR_SIZE / Math.max(img.width, img.height)
    setTransform({ x: 0, y: 0, scale })
  }

  const adjustZoom = (factor) => {
    setTransform((prev) => ({
      ...prev,
      scale: Math.min(MAX_SCALE, Math.max(MIN_SCALE, prev.scale * factor)),
    }))
  }

  const getMidpoint = (touches) => ({
    x: (touches[0].clientX + touches[1].clientX) / 2,
    y: (touches[0].clientY + touches[1].clientY) / 2,
  })

  const getDistance = (touches) => {
    const dx = touches[0].clientX - touches[1].clientX
    const dy = touches[0].clientY - touches[1].clientY
    return Math.hypot(dx, dy)
  }

  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      touchRef.current = {
        mode: 'pan',
        start: { x: e.touches[0].clientX, y: e.touches[0].clientY },
        startTransform: { ...transform },
      }
    } else if (e.touches.length === 2) {
      touchRef.current = {
        mode: 'pinch',
        startDist: getDistance(e.touches),
        startScale: transform.scale,
        startTransform: { ...transform },
        startMid: toEditorPoint(...Object.values(getMidpoint(e.touches))),
      }
    }
  }

  const handleTouchMove = useCallback((e) => {
    e.preventDefault()
    const state = touchRef.current
    if (!state) return

    if (state.mode === 'pinch' && e.touches.length >= 2) {
      const newDist = getDistance(e.touches)
      const midClient = getMidpoint(e.touches)
      const newMid = toEditorPoint(midClient.x, midClient.y)
      const factor = newDist / state.startDist
      const newScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, state.startScale * factor))

      setTransform(() => {
        const centerOffsetX = state.startMid.x - EDITOR_SIZE / 2
        const centerOffsetY = state.startMid.y - EDITOR_SIZE / 2
        return {
          x: state.startTransform.x + centerOffsetX * (1 - newScale / state.startScale) + (newMid.x - state.startMid.x),
          y: state.startTransform.y + centerOffsetY * (1 - newScale / state.startScale) + (newMid.y - state.startMid.y),
          scale: newScale,
        }
      })
    } else if (state.mode === 'pan' && e.touches.length === 1) {
      const rect = canvasRef.current.getBoundingClientRect()
      const dx = (e.touches[0].clientX - state.start.x) * (EDITOR_SIZE / rect.width)
      const dy = (e.touches[0].clientY - state.start.y) * (EDITOR_SIZE / rect.height)
      setTransform((prev) => ({
        ...prev,
        x: state.startTransform.x + dx,
        y: state.startTransform.y + dy,
      }))
    }
  }, [img, gridSize])

  // 原生事件监听必须放在 handleWheel / handleTouchMove 定义之后，避免 TDZ
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const onWheel = (e) => handleWheel(e)
    const onTouchMove = (e) => handleTouchMove(e)
    canvas.addEventListener('wheel', onWheel, { passive: false })
    canvas.addEventListener('touchmove', onTouchMove, { passive: false })
    return () => {
      canvas.removeEventListener('wheel', onWheel)
      canvas.removeEventListener('touchmove', onTouchMove)
    }
  }, [handleWheel, handleTouchMove])

  const handleTouchEnd = () => {
    touchRef.current = null
    setDragging(false)
  }

  // 锁定后由当前缩放反推的板子规格（格子数）
  // 注意：必须声明在 applyCellEdit 之前，否则依赖数组引用会触发 TDZ 错误
  const lockedGridSize = useMemo(() => {
    const lockGeo = getLockGeometry()
    if (!lockGeo) return null
    return Math.max(1, Math.round(EDITOR_SIZE / lockGeo.cell))
  }, [getLockGeometry])

  // 锁定后板子规格随缩放实时上报给父组件（用于尺寸显示）
  useEffect(() => {
    if (locked && lockedGridSize) {
      onLiveGridSizeChange?.(lockedGridSize)
    }
  }, [locked, lockedGridSize, onLiveGridSizeChange])

  // 应用单粒大小调整（供父组件调用）
  const applyCellEdit = useCallback(() => {
    const newGridSize = locked && lockedGridSize
      ? lockedGridSize
      : Math.max(5, Math.round(EDITOR_SIZE / cellSize))
    onGridSizeChange?.(newGridSize)
    // 不重置缩放/位置：保持用户当前视角，锁定后锚点按当前画面捕获，
    // 之后继续缩放编辑时预览会跟随同步缩放
    setCellEditMode(false)
  }, [locked, lockedGridSize, cellSize, onGridSizeChange])

  // 取消单粒大小调整（供父组件调用）
  const cancelCellEdit = useCallback(() => {
    setCellEditMode(false)
  }, [])

  const handleGenerate = () => {
    if (!img || !previewPattern) return
    // 直接复用实时预览的结果，确保所见即所得
    onGenerate(previewPattern)
  }

  const visibleGridSpan = useMemo(() => {
    if (!img) return null
    const cellPx = EDITOR_SIZE / gridSize
    return {
      cols: Math.max(1, Math.round((img.width * transform.scale) / cellPx)),
      rows: Math.max(1, Math.round((img.height * transform.scale) / cellPx)),
    }
  }, [img, transform.scale, gridSize])

  // 暴露方法给父组件
  useImperativeHandle(ref, () => ({
    applyCellEdit,
    cancelCellEdit,
  }), [applyCellEdit, cancelCellEdit])

  if (!src) return null

  return (
    <div className="space-y-3">
      {/* 单粒大小引导提示 */}
      {showSizeGuide && !cellEditMode && (
        <div className="mb-3 flex items-center justify-between rounded-xl border border-peach/40 bg-peach-light/40 px-4 py-2.5">
          <div className="flex items-center gap-2">
            <span className="text-lg">📏</span>
            <span className="text-sm text-cocoa">
              <b>重要：</b>调整单粒大小，让图纸格子数匹配你的拼豆板规格
            </span>
          </div>
          <button
            onClick={() => setShowSizeGuide(false)}
            className="text-xs text-cocoa-light transition hover:text-cocoa"
          >
            知道了
          </button>
        </div>
      )}

      {/* 画板尺寸控制（由父组件传入）：位于重要提示下方、编辑画板上方 */}
      {headerControl}

      <div className="grid gap-4 lg:grid-cols-2">
        <div>
          <div className="relative overflow-hidden rounded-2xl border border-lavender/30 bg-white">
            <div className="absolute right-2 top-2 z-10 inline-flex overflow-hidden rounded-lg border border-lavender/50 bg-white/95 shadow-sm">
              <button type="button" onClick={() => adjustZoom(1 / 1.15)} className="flex h-8 w-8 items-center justify-center text-cocoa transition hover:bg-lavender/30" aria-label="缩小图片" title="缩小图片">
                <ZoomOut size={16} aria-hidden="true" />
              </button>
              <button type="button" onClick={handleReset} className="flex h-8 w-8 items-center justify-center border-x border-lavender/40 text-cocoa transition hover:bg-lavender/30" aria-label="适配画布" title="适配画布">
                <Maximize2 size={16} aria-hidden="true" />
              </button>
              <button type="button" onClick={() => adjustZoom(1.15)} className="flex h-8 w-8 items-center justify-center text-cocoa transition hover:bg-lavender/30" aria-label="放大图片" title="放大图片">
                <ZoomIn size={16} aria-hidden="true" />
              </button>
            </div>
            <canvas
              ref={canvasRef}
              width={EDITOR_SIZE}
              height={EDITOR_SIZE}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={stopDrag}
              onMouseLeave={stopDrag}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
              className={`w-full ${dragging ? 'cursor-grabbing' : 'cursor-grab'}`}
              style={{ imageRendering: 'auto', touchAction: 'none' }}
            />
          </div>

          {/* 工具栏：编辑画板下方、实时预览上方 */}
          <div className="mt-3 flex flex-wrap items-center gap-3">
            {!cellEditMode ? (
              <>
                <span className="text-xs text-cocoa-light">缩放 {(transform.scale * 100).toFixed(0)}%</span>
                {locked && lockedGridSize ? (
                  <span className="text-xs font-medium text-blush-dark">
                    🔒 锚点已锁定 · 当前约 {lockedGridSize}×{lockedGridSize} 格
                  </span>
                ) : (
                  visibleGridSpan && (
                    <span className="text-xs text-cocoa-light">
                      整体约 {visibleGridSpan.cols}×{visibleGridSpan.rows} 格
                    </span>
                  )
                )}
                <button
                  onClick={handleReset}
                  className="inline-flex items-center gap-1 rounded-full border border-lavender/60 px-3 py-1 text-xs font-medium text-cocoa transition hover:bg-lavender/30"
                >
                  <RotateCcw size={13} aria-hidden="true" />
                  重置位置
                </button>
                <button
                  onClick={() => {
                    if (locked) return
                    setCellSize(Math.max(40, EDITOR_SIZE / gridSize))
                    setCellEditMode(true)
                    setShowSizeGuide(false)
                  }}
                  disabled={locked}
                  className="rounded-full bg-peach px-4 py-1.5 text-sm font-semibold text-white shadow-soft transition hover:bg-peach-dark hover:shadow-md disabled:opacity-50"
                >
                  📏 调整单粒大小
                </button>
                {toolbarControl}
              </>
            ) : (
              <>
                <span className="text-xs text-cocoa-light">
                  {locked && lockedGridSize
                    ? `🔒 锚点已锁定 · 单粒随图缩放 · 当前约 ${lockedGridSize}×${lockedGridSize} 格`
                    : `单粒大小 ${(EDITOR_SIZE / Math.max(5, Math.round(EDITOR_SIZE / cellSize))).toFixed(1)}px · 画幅规格约 ${Math.max(5, Math.round(EDITOR_SIZE / cellSize))} × ${Math.max(5, Math.round(EDITOR_SIZE / cellSize))} 格`}
                </span>
                {toolbarControl}
              </>
            )}
          </div>
        </div>

        <div className="flex flex-col">
          <p className="mb-2 text-xs font-medium text-cocoa-light">实时预览（颗粒 × 色彩）</p>
          <canvas
            ref={previewCanvasRef}
            className="w-full flex-1 rounded-2xl border border-lavender/30 bg-cream-100 object-contain"
            style={{ imageRendering: 'pixelated' }}
          />
          <p className="mt-2 text-xs text-cocoa-light">{previewPattern?.legend.length || 0} 种颜色</p>
          {!cellEditMode && (
            <button
              onClick={handleGenerate}
              className="mt-3 w-full rounded-full bg-blush py-2.5 text-sm font-semibold text-white shadow-soft transition hover:bg-blush-dark hover:shadow-md"
            >
              生成图纸并进入编辑
            </button>
          )}
        </div>
      </div>
    </div>
  )
})

export default BeadEditor
